<template>
  <div class="glassmorphism-bg">
    <v-main>
      <v-container fluid class="pa-6">
        <!-- Header Card -->
        <div class="glassmorphism-card mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="page-title">Notifications</h2>
            <div class="d-flex gap-2">
              <v-btn
                color="primary"
                rounded
                elevation="0"
                class="glassmorphism-btn primary-btn"
                @click="markAllAsRead"
                :disabled="!hasUnreadNotifications"
              >
                <v-icon left>mdi-check-all</v-icon>
                Mark All Read
              </v-btn>
              <v-btn
                color="error"
                rounded
                elevation="0"
                class="glassmorphism-btn error-btn"
                @click="clearAllNotifications"
                :disabled="notifications.length === 0"
              >
                <v-icon left>mdi-delete-sweep</v-icon>
                Clear All
              </v-btn>
            </div>
          </div>

          <!-- Filter Section -->
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="filter.type"
                :items="notificationTypes"
                label="Filter by Type"
                outlined
                rounded
                dense
                clearable
                class="glassmorphism-input"
                prepend-inner-icon="mdi-filter"
                @input="filterNotifications"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="filter.status"
                :items="statusTypes"
                label="Filter by Status"
                outlined
                rounded
                dense
                clearable
                class="glassmorphism-input"
                prepend-inner-icon="mdi-eye"
                @input="filterNotifications"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Notifications List -->
        <div class="glassmorphism-card">
          <div v-if="isLoading" class="text-center py-12">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <p class="text-h6 mt-4 text--secondary">Loading notifications...</p>
          </div>

          <div v-else-if="filteredNotifications.length === 0" class="text-center py-12">
            <v-icon size="80" color="grey lighten-1">mdi-bell-outline</v-icon>
            <p class="text-h5 mt-4 text--secondary">No notifications</p>
            <p class="text-body-1 text--secondary">You're all caught up!</p>
          </div>

          <div v-else>
            <v-list class="glassmorphism-list">
              <template v-for="(notification, index) in filteredNotifications">
                <v-list-item
                  :key="notification.id"
                  :class="[
                    'notification-item',
                    { 'unread-notification': !notification.read }
                  ]"
                  @click="markAsRead(notification)"
                >
                  <v-list-item-avatar :color="getNotificationColor(notification.type)">
                    <v-icon :color="getNotificationIconColor(notification.type)">
                      {{ getNotificationIcon(notification.type) }}
                    </v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="notification-title">
                      {{ notification.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="notification-message">
                      {{ notification.message }}
                    </v-list-item-subtitle>
                    <div class="notification-meta mt-2">
                      <v-chip 
                        x-small 
                        :color="getNotificationColor(notification.type)"
                        text-color="white"
                        class="mr-2"
                      >
                        {{ notification.type }}
                      </v-chip>
                      <span class="notification-time">
                        {{ formatTime(notification.created_at) }}
                      </span>
                    </div>
                  </v-list-item-content>

                  <v-list-item-action>
                    <div class="d-flex flex-column align-center">
                      <v-btn
                        icon
                        small
                        color="error"
                        class="glassmorphism-icon-btn mb-2"
                        @click.stop="deleteNotification(notification.id)"
                      >
                        <v-icon size="18">mdi-delete</v-icon>
                      </v-btn>
                      <v-icon
                        v-if="!notification.read"
                        color="primary"
                        size="12"
                      >
                        mdi-circle
                      </v-icon>
                    </div>
                  </v-list-item-action>
                </v-list-item>

                <v-divider v-if="index < filteredNotifications.length - 1" :key="`divider-${index}`"></v-divider>
              </template>
            </v-list>

            <!-- Pagination -->
            <div class="d-flex justify-center mt-6" v-if="notifications.length > itemsPerPage">
              <v-pagination
                v-model="currentPage"
                :length="totalPages"
                :total-visible="7"
                color="primary"
                class="glassmorphism-pagination"
              ></v-pagination>
            </div>
          </div>
        </div>

        <!-- Success Dialog -->
        <v-dialog v-model="showSuccessDialog" width="400">
          <v-card rounded="xxl" class="glassmorphism-card">
            <v-card-text class="text-center py-6">
              <v-icon size="60" color="success">mdi-check-circle</v-icon>
              <h3 class="mt-4">{{ successMessage }}</h3>
              <v-btn
                color="success"
                rounded
                class="glassmorphism-btn mt-4"
                @click="showSuccessDialog = false"
              >
                OK
              </v-btn>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import mixin from "@/mixin";

export default {
  mixins: [mixin],
  name: "NotificationsPage",
  data() {
    return {
      isLoading: false,
      notifications: [],
      filteredNotifications: [],
      filter: {
        type: null,
        status: null
      },
      notificationTypes: [
        { text: 'All Types', value: null },
        { text: 'System', value: 'system' },
        { text: 'User Action', value: 'user_action' },
        { text: 'Content Moderation', value: 'moderation' },
        { text: 'Security', value: 'security' },
        { text: 'Info', value: 'info' }
      ],
      statusTypes: [
        { text: 'All', value: null },
        { text: 'Unread', value: 'unread' },
        { text: 'Read', value: 'read' }
      ],
      currentPage: 1,
      itemsPerPage: 10,
      showSuccessDialog: false,
      successMessage: ''
    };
  },
  computed: {
    hasUnreadNotifications() {
      return this.notifications.some(n => !n.read);
    },
    totalPages() {
      return Math.ceil(this.filteredNotifications.length / this.itemsPerPage);
    }
  },
  mounted() {
    this.loadNotifications();
  },
  methods: {
    async loadNotifications() {
      this.isLoading = true;
      try {
        // Mock data for demonstration - replace with actual API call
        await this.delay(1000); // Simulate API delay
        this.notifications = this.generateMockNotifications();
        this.filterNotifications();
      } catch (error) {
        console.error('Error loading notifications:', error);
      } finally {
        this.isLoading = false;
      }
    },
    generateMockNotifications() {
      return [
        {
          id: 1,
          title: 'New User Registered',
          message: 'A new user has registered on the platform and requires approval.',
          type: 'user_action',
          read: false,
          created_at: new Date(Date.now() - 3600000) // 1 hour ago
        },
        {
          id: 2,
          title: 'Content Flagged for Review',
          message: 'Post #1234 has been flagged by multiple users and needs moderation.',
          type: 'moderation',
          read: false,
          created_at: new Date(Date.now() - 7200000) // 2 hours ago
        },
        {
          id: 3,
          title: 'System Maintenance Complete',
          message: 'Scheduled system maintenance has been completed successfully.',
          type: 'system',
          read: true,
          created_at: new Date(Date.now() - 86400000) // 1 day ago
        },
        {
          id: 4,
          title: 'Security Alert',
          message: 'Multiple failed login attempts detected from IP 192.168.1.100.',
          type: 'security',
          read: false,
          created_at: new Date(Date.now() - 10800000) // 3 hours ago
        },
        {
          id: 5,
          title: 'Welcome to Vijan.AI',
          message: 'Thank you for joining our AI agents marketplace.',
          type: 'info',
          read: true,
          created_at: new Date(Date.now() - 172800000) // 2 days ago
        }
      ];
    },
    filterNotifications() {
      let filtered = [...this.notifications];
      
      if (this.filter.type) {
        filtered = filtered.filter(n => n.type === this.filter.type);
      }
      
      if (this.filter.status) {
        if (this.filter.status === 'read') {
          filtered = filtered.filter(n => n.read);
        } else if (this.filter.status === 'unread') {
          filtered = filtered.filter(n => !n.read);
        }
      }
      
      this.filteredNotifications = filtered;
      this.currentPage = 1;
    },
    getNotificationColor(type) {
      const colors = {
        system: 'blue',
        user_action: 'green',
        moderation: 'orange',
        security: 'red',
        info: 'purple'
      };
      return colors[type] || 'grey';
    },
    getNotificationIconColor() {
      return 'white';
    },
    getNotificationIcon(type) {
      const icons = {
        system: 'mdi-cog',
        user_action: 'mdi-account-plus',
        moderation: 'mdi-shield-alert',
        security: 'mdi-security',
        info: 'mdi-information'
      };
      return icons[type] || 'mdi-bell';
    },
    formatTime(date) {
      const now = new Date();
      const diffMs = now - new Date(date);
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) {
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else {
        return `${diffDays}d ago`;
      }
    },
    async markAsRead(notification) {
      if (!notification.read) {
        notification.read = true;
        // Here you would make an API call to mark as read
        console.log(`Marking notification ${notification.id} as read`);
      }
    },
    async markAllAsRead() {
      this.notifications.forEach(n => n.read = true);
      this.successMessage = 'All notifications marked as read';
      this.showSuccessDialog = true;
      // Here you would make an API call to mark all as read
      console.log('Marking all notifications as read');
    },
    async deleteNotification(id) {
      this.notifications = this.notifications.filter(n => n.id !== id);
      this.filterNotifications();
      this.successMessage = 'Notification deleted successfully';
      this.showSuccessDialog = true;
      // Here you would make an API call to delete the notification
      console.log(`Deleting notification ${id}`);
    },
    async clearAllNotifications() {
      this.notifications = [];
      this.filteredNotifications = [];
      this.successMessage = 'All notifications cleared';
      this.showSuccessDialog = true;
      // Here you would make an API call to clear all notifications
      console.log('Clearing all notifications');
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  }
};
</script>

<style scoped>
.glassmorphism-bg {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.glassmorphism-card {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.glassmorphism-input {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
}

.glassmorphism-input >>> .v-input__control .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.2) !important;
}

.glassmorphism-input >>> .v-input__control .v-input__slot:hover {
  border-color: rgba(102, 126, 234, 0.4) !important;
}

.glassmorphism-input >>> .v-input--is-focused .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2) !important;
}

.glassmorphism-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 15px 0 rgba(31, 38, 135, 0.2) !important;
}

.primary-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

.error-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: white !important;
}

.glassmorphism-list {
  background: transparent !important;
  border-radius: 12px;
}

.notification-item {
  border-radius: 12px !important;
  margin-bottom: 8px !important;
  padding: 16px !important;
  transition: all 0.3s ease !important;
  cursor: pointer;
}

.notification-item:hover {
  background: rgba(102, 126, 234, 0.1) !important;
  transform: translateX(4px);
}

.unread-notification {
  background: rgba(102, 126, 234, 0.05) !important;
  border-left: 4px solid #667eea;
}

.notification-title {
  font-weight: 600 !important;
  color: #2c3e50 !important;
  font-size: 16px !important;
}

.notification-message {
  color: #64748b !important;
  font-size: 14px !important;
  margin-top: 4px !important;
}

.notification-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.notification-time {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
}

.glassmorphism-icon-btn {
  background: rgba(255, 255, 255, 0.3) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  transition: all 0.3s ease !important;
}

.glassmorphism-icon-btn:hover {
  background: rgba(255, 255, 255, 0.5) !important;
  transform: scale(1.05);
}

.glassmorphism-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-weight: 500;
}

.glassmorphism-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* Responsive */
@media (max-width: 960px) {
  .glassmorphism-card {
    padding: 16px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .notification-item {
    padding: 12px !important;
  }
}
</style>